import { AllowLocalNetworkAccessValue, EmbedCodeTokens } from "../types";
import PlaylistEmbedV7 from "./PlaylistEmbedV7";
import PlaylistSecureEmbed from "./playlistSecureEmbed";
import EmbedV7 from "./EmbedV7";
import { translate } from "@kaltura/mediaspace-shared-utils";
import SecureEmbed from "./SecureEmbed";
import { EntryType } from "@kaltura/mediaspace-shared-types";

export interface EmbedSettingsAvailableFeatures {
    embedSizes?: boolean;
    autoPlay?: boolean;
    thumbnailEmbed?: boolean;
    timeSelect?: boolean;
    embedCodeText?: boolean;
    setAsTypeDefault?: boolean;
}

/**
 * "static" props used for generating embed code
 */
export interface EmbedCodeProps {
    host: string;
    partnerId: number;
    secureEmbed?: boolean;
    omitSandboxOnIframe?: boolean;
    allowLocalNetworkAccess?: AllowLocalNetworkAccessValue;
    title?: string;
}

export interface PlayerV7Config {
    provider: { widgetId: string; partnerId: number; uiConfId: string };
    entry_id?: string;
    playlist_id?: string;
    uiConfPluginVersions?: string;
}

export default class EmbedHelperV7 {
    static getPlaylistEmbedParams = (
        playerId: string,
        selectedEmbedSize: string,
        entryType: string,
        playerV7Config: PlayerV7Config,
        embedCodeParams: EmbedCodeProps
    ): EmbedCodeTokens => {
        const size = selectedEmbedSize.split("x");
        return {
            WIDGET_ID: playerV7Config.provider.widgetId,
            WIDTH: parseInt(size[0], 10),
            HEIGHT: parseInt(size[1], 10),
            HOST: embedCodeParams.host,
            PARTNER_ID: embedCodeParams.partnerId,
            UICONF_ID: playerId,
            PLAYLIST_ID: playerV7Config.playlist_id,
            ENTRY_ID: playerV7Config.entry_id,
            RESPONSIVE: true,
            OMIT_SANDBOX: false,
            ALLOW_LOCAL_NETWORK_ACCESS: embedCodeParams.allowLocalNetworkAccess ?? AllowLocalNetworkAccessValue.Off,
            TITLE: embedCodeParams.title || translate("Kaltura Player"),
            UICONF_PLUGIN_VERSIONS: playerV7Config.uiConfPluginVersions ?? "",
        };
    };

    static getEmbedParams = (
        playerId: string,
        selectedEmbedSize: string,
        entryType: string,
        playerV7Config: PlayerV7Config,
        embedCodeParams: EmbedCodeProps,
        startTime?: number
    ) => {
        const size = selectedEmbedSize.split("x");
        return {
            WIDGET_ID: playerV7Config.provider.widgetId,
            WIDTH: size[0],
            HEIGHT: size[1],
            HOST: embedCodeParams.host,
            PARTNER_ID: embedCodeParams.partnerId,
            UICONF_ID: playerId,
            ENTRY_ID: playerV7Config.entry_id,
            TITLE: embedCodeParams.title || translate("Kaltura Player"),
            START_TIME: startTime,
            OMIT_SANDBOX: embedCodeParams.omitSandboxOnIframe ?? false,
            ALLOW_LOCAL_NETWORK_ACCESS: embedCodeParams.allowLocalNetworkAccess ?? AllowLocalNetworkAccessValue.Off,
            ENTRY_TYPE: entryType,
        };
    };

    static generateEmbedCodeV7(
        playerId: string,
        selectedEmbedSize: string,
        mediaType: EntryType,
        embedCodeParams: EmbedCodeProps,
        playerV7Config?: PlayerV7Config,
        startTime?: number
    ) {
        let embedCode = "";

        if (!playerV7Config) {
            return embedCode;
        }

        // V7 playlist player
        if (mediaType === EntryType.playlist) {
            const params = EmbedHelperV7.getPlaylistEmbedParams(
                playerId,
                selectedEmbedSize,
                mediaType,
                playerV7Config,
                embedCodeParams
            );

            if (embedCodeParams.secureEmbed) {
                embedCode = PlaylistSecureEmbed.playlistSecureEmbedCode(params);
            }
            else {
                embedCode = PlaylistEmbedV7.playlistEmbedCodeV7(params);
            }
        }
        else {
            // V7 media (not playlist) player
            const embedParams = this.getEmbedParams(
                playerId,
                selectedEmbedSize,
                mediaType,
                playerV7Config,
                embedCodeParams,
                startTime
            );

            if (embedCodeParams.secureEmbed) {
                embedCode = SecureEmbed.secureEmbed(embedParams);
            }
            else {
                embedCode = EmbedV7.EmbedCodeV7(embedParams);
            }
        }
        return embedCode;
    }
}
